import Clarity from "@microsoft/clarity";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Dispatch } from "redux";
import { EXTERNAL_LOGIN_URL } from "../consts";
import {
  LexIpAuthTokenPayload,
  LexSsoAuthTokenPayload,
  LexTokenPayload,
} from "../models";
import { authByIp } from "../services";
import { doLogout, loginWithToken } from "../store/reducer/_sessionReducer";
import { RootState } from "../store/reducer/_store";
import {
  getIsIpDomain,
  removeLoggedStatus,
  removeLoggedUser,
  removeToken,
} from "../utils";
import { ssoUserManager } from "../utils/sso";

export function useValidateToken() {
  const dispatch = useDispatch();

  const isIpDomain = getIsIpDomain();

  const token = useSelector((state: RootState) => state.sessionContext.token);
  const loggedUser = useSelector(
    (state: RootState) => state.sessionContext.loggedUser,
  );

  const { token: tokenFromRoute } = useParams<{ token?: string }>();
  const pathName = window.location.pathname;

  const isAuthRoute =
    pathName.startsWith("/autenticar") ||
    pathName.startsWith("/login") ||
    pathName.startsWith("/esqueci-minha-senha") ||
    pathName.startsWith("/resetar-senha") ||
    pathName.startsWith("/sso/callback") ||
    pathName.startsWith("/sso/logout") ||
    pathName.startsWith("/limite-acessos") ||
    pathName.startsWith("/limite-acessos-usuario") ||
    pathName.startsWith("/acesso-indisponivel");

  const hasValidatedRef = useRef(false);

  const handleInvalidToken = useCallback(
    async (dispatch: Dispatch, token?: string) => {
      let decoded:
        | LexTokenPayload
        | LexIpAuthTokenPayload
        | LexSsoAuthTokenPayload
        | null = null;

      try {
        if (token) {
          decoded = jwtDecode(token);
        }
      } catch {}

      removeLoggedStatus();
      removeLoggedUser();
      removeToken();

      dispatch(doLogout());

      const isSSOUser =
        decoded && "type" in decoded && decoded.type === "sso_user";

      if (isSSOUser) {
        try {
          const ssoDecoded = decoded as LexSsoAuthTokenPayload;

          if (ssoDecoded.provider === "AASP")
            await ssoUserManager.signinRedirect();

          window.location.href = ssoDecoded.redirectUrl || "/login";
          return;
        } catch {}
      }

      if (!isAuthRoute) {
        if (isIpDomain) {
          return window.location.replace("/login");
        }

        window.location.href = EXTERNAL_LOGIN_URL;
      }
    },
    [isAuthRoute],
  );

  const handleValid = useCallback(
    (token: string) => {
      try {
        const decoded = jwtDecode<
          LexTokenPayload | LexIpAuthTokenPayload | LexSsoAuthTokenPayload
        >(token);
        const nowInSeconds = Date.now() / 1000;

        if (!decoded.exp || decoded.exp < nowInSeconds) {
          handleInvalidToken(dispatch, token);
          return;
        }

        if (
          "type" in decoded &&
          (decoded.type === "ip_auth" || decoded.type === "dependent_auth")
        ) {
          Clarity.identify(decoded.accountId);
          Clarity.setTag("user_email", decoded.email);
          Clarity.setTag("user_origin", "ip_auth");

          Clarity.event("Login success (IP)");

          if (!loggedUser) {
            dispatch(
              loginWithToken({
                user: {
                  nome: decoded.name,
                  email: decoded.email,
                },
                token,
              }),
            );
          }

          return;
        }

        if ("type" in decoded && decoded.type === "sso_user") {
          Clarity.identify(decoded.providerUserId);
          Clarity.setTag("user_email", decoded.email);
          Clarity.setTag("user_origin", decoded.type);
          Clarity.setTag(
            "user_sso_provider",
            decoded?.provider || "Não identificado",
          );

          Clarity.event("Login success (SSO)");

          if (!loggedUser) {
            dispatch(
              loginWithToken({
                user: {
                  nome: decoded.name,
                  email: decoded.email,
                },
                token,
              }),
            );
          }

          return;
        }

        const decodedToken = decoded as LexTokenPayload;

        Clarity.identify(String(decodedToken.dados.IDPessoa));
        Clarity.setTag("user_email", decodedToken.cliente.email);
        Clarity.setTag("user_origin", decodedToken.origem);

        Clarity.event("Login success");

        if (!loggedUser) {
          dispatch(
            loginWithToken({
              user: decodedToken.cliente,
              token,
            }),
          );
        }
      } catch {
        handleInvalidToken(dispatch);
      }
    },
    [dispatch, loggedUser, handleInvalidToken],
  );

  const handleAuthByIp = async () => {
    try {
      const { token } = await authByIp();

      const decoded = jwtDecode(token) as LexIpAuthTokenPayload;

      const user = {
        name: decoded.name,
        email: decoded.email,
        origem: decoded.type,
        idPessoa: decoded.accountId,
      };

      Clarity.identify(String(decoded.accountId));
      Clarity.setTag("user_email", decoded.email);
      Clarity.setTag("user_origin", decoded.type);

      dispatch(
        loginWithToken({
          user,
          token,
        }),
      );

      if (!window.location.pathname.startsWith("/home")) {
        window.location.href = `${window.location.origin}/home`;

        return;
      }
    } catch (error) {
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = `${window.location.origin}/login`;
      }
    }
  };

  useEffect(() => {
    if (hasValidatedRef.current) return;
    hasValidatedRef.current = true;

    if (token) {
      handleValid(token);
      return;
    }
    if (
      !token &&
      isIpDomain &&
      !pathName.startsWith("/login") &&
      !pathName.startsWith("/sso/callback") &&
      !pathName.startsWith("/limite-acessos") &&
      !pathName.startsWith("/esqueci-minha-senha") &&
      !pathName.startsWith("/resetar-senha") &&
      !pathName.startsWith("/acesso-indisponivel")
    ) {
      handleAuthByIp();
    }

    if (!isAuthRoute) redirectToExternalLogin();
  }, [token, tokenFromRoute, handleValid, isAuthRoute]);

  function redirectToExternalLogin() {
    if (isIpDomain) return;

    window.location.href = EXTERNAL_LOGIN_URL;
  }
}
